import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Popover, Row, Spin } from 'antd';
import { floatToPercentageString } from '../../utils/DataFormatterUtils';
import moment from 'moment';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { InfoCircleOutlined } from '@ant-design/icons';
import DashboardValueChangeDisplay from './DashboardValueChangeDisplay';
import { withRouter } from 'react-router';
import { Form } from '@ant-design/compatible';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ErrorBoundary } from '@sentry/react';

require('./DashboardKPICard.less');

const buildUrl = (baseUrl, ...queryStrings) => {
	const cleanQueries = queryStrings.filter(Boolean).join('&');
	return cleanQueries ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${cleanQueries}` : baseUrl;
};

const formatValue = (type, value) => {
	switch (type) {
		case 'rate':
			return `${(value * 100).toFixed(0)}%`;
		case 'time':
			if (value !== 0) {
				return moment.duration(value, 'seconds').humanize();
			} else {
				return 0;
			}
		default:
			return value;
	}
};

const DashboardKPICard = ({
	kpi,
	startDate,
	endDate,
	showComparison = false,
	companyConfig,
	userType,
	currentUser,
	filters,

	getCount,
}): React.ReactElement => {
	const { type, fnToCall, seeDetailsURL, displayName, id, info } = kpi;
	const [value, setValue] = useState(null);
	const [pastValue, setPastValue] = useState(null);
	const [loading, setLoading] = useState(true);
	const [detailParams, setDetailParams] = useState(``);

	useEffect(() => {
		if (fnToCall) {
			setLoading(true);
			getCount(fnToCall(filters)).then((data) => {
				setLoading(false);
				setValue(data.value);
				setPastValue(data.pastValue);
				if (data.detailParams) {
					setDetailParams(`&${data.detailParams}`);
				}
			});
		}
	}, [seeDetailsURL, filters]);

	const buyerCompanySettings =
		userType === ROLE_TYPES.SUPPLIER
			? nullSafeGetOrElse('facility.privateBuyerCompanySettings', currentUser, {})
			: companyConfig;
	const card = (
		<Card style={{ height: '100%' }} className={seeDetailsURL ? 'dashboardKPICard' : null}>
			<Spin spinning={loading}>
				<Row
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
						marginBottom: 8,
					}}
				>
					<Col>
						{displayName}
						{info ? (
							<Popover style={{ marginTop: '-8px' }} content={info(buyerCompanySettings)}>
								<span style={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)', marginLeft: 8 }}>
									<InfoCircleOutlined />
								</span>
							</Popover>
						) : null}
					</Col>
				</Row>
				{loading
					? null
					: [
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'flex-end',
									marginBottom: 24,
								}}
							>
								<div style={{ lineHeight: 1, fontSize: '24px' }}>{formatValue(type, value)}</div>
							</div>,
							showComparison ? (
								<div>
									<DashboardValueChangeDisplay
										value={value}
										pastValue={pastValue}
										kpi={kpi}
										startDate={startDate}
										endDate={endDate}
									/>
								</div>
							) : null,
					  ]}
			</Spin>
		</Card>
	);
	const filterQueryParams = useMemo(() => new URLSearchParams(filters).toString(), [filters]);

	const fullUrl =
		(seeDetailsURL && buildUrl(seeDetailsURL, detailParams, filterQueryParams)) || null;
	return (
		<ErrorBoundary fallback={null}>
			<Col key={id} span={6} style={{ marginBottom: 16 }}>
				{seeDetailsURL ? <Link to={fullUrl}>{card}</Link> : card}
			</Col>
		</ErrorBoundary>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	attentionDashboard: state.attention_dashboards.detail,
});

const mapDispatchToProps = (dispatch) => ({
	getCount: (fn) => dispatch(fn),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(DashboardKPICard)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
		companyConfig: (state as any).company_config.detail,
	}),
	() => ({})
)(Form.create()(ComponentWithoutUserType));
